import React, {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {ChevronLeft, Info, MonitorSmartphone, RefreshCw} from "lucide-react";
import Button from "../../Common/Button";
import {ListLoader, SectionActions, SettingsTable} from "../SettingsUI";
import PosDeviceDetail from "./PosDeviceDetail";
import {ConnectionBadge, PendingBadge} from "./PosConfigUI";
import {fetchPosConfig, fetchPosConfigs, lastBackupDate, PLATFORM_LABELS} from "./posConfig";

const formatDateTime = (date) => !date ? "—" : date.toLocaleString("fr-FR", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

const ReadOnlyNotice = () => (
    <div className="flex items-start gap-3 px-4 sm:px-6 py-3 bg-primary/5 dark:bg-primary/10 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary"/>
        <p>
            Ces réglages sont propres à chaque caisse. Ce que vous modifiez ici est appliqué par la caisse tablette
            à sa prochaine synchronisation (environ une minute). Une modification faite ensuite sur la caisse l'emporte.
        </p>
    </div>
);

const ErrorMessage = ({error}) => (
    <p className="p-4 text-center text-red-500">
        Erreur : {error.response?.data?.error || error.response?.data?.detail || error.message}
    </p>
);

const DeviceView = ({id, onBack}) => {
    const {data: config, isLoading, error, refetch, isFetching} = useQuery({
        queryKey: ["pos-config", id],
        queryFn: () => fetchPosConfig(id),
        // Watch the device apply the pending changes (it syncs every minute)
        // and keep its « Connectée » badge current.
        refetchInterval: (query) => (query.state.data?.pendingChanges ? 20000 : 60000),
    });

    return (
        <div>
            <SectionActions>
                <Button variant="plain" size="sm" icon={RefreshCw} loading={isFetching} onClick={() => refetch()}>
                    Actualiser
                </Button>
            </SectionActions>
            <div className="flex items-center gap-3 px-4 sm:px-6 h-16 border-b border-gray-200 dark:border-gray-700">
                <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack}>Appareils</Button>
                {config && (
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {config.device?.name || "Appareil sans nom"}
                            {config.device?.registerName && <span className="text-primary"> · {config.device.registerName}</span>}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {PLATFORM_LABELS[config.device?.platform] || "Plateforme inconnue"}
                            {config.device?.appVersion && ` · v${config.device.appVersion}`}
                            {` · Sauvegardé le ${formatDateTime(lastBackupDate(config))}`}
                        </p>
                    </div>
                )}
                {config && <span className="ml-auto"><ConnectionBadge config={config}/></span>}
            </div>
            {isLoading ? (
                <ListLoader label="Chargement des réglages..."/>
            ) : error ? (
                <ErrorMessage error={error}/>
            ) : !config ? (
                <p className="p-4 text-center text-gray-500">Sauvegarde introuvable.</p>
            ) : (
                <PosDeviceDetail config={config}/>
            )}
        </div>
    );
};

const PosDevicesSettings = () => {
    const [selectedId, setSelectedId] = useState(null);
    const {data: configs = [], isLoading, error, refetch, isFetching} = useQuery({
        queryKey: ["pos-configs"],
        queryFn: fetchPosConfigs,
        enabled: !selectedId,
        refetchInterval: 60000,
    });

    if (selectedId) {
        return (
            <>
                <ReadOnlyNotice/>
                <DeviceView id={selectedId} onBack={() => setSelectedId(null)}/>
            </>
        );
    }

    const columns = [
        {key: "name", label: "Appareil", render: c => (
            <span className="inline-flex items-center gap-2">
                {c.device?.name || "Appareil sans nom"}
                {c.device?.registerName && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">{c.device.registerName}</span>
                )}
                {c.pendingChanges && <PendingBadge/>}
            </span>
        )},
        {key: "connection", label: "Connexion", render: c => <ConnectionBadge config={c}/>},
        {key: "platform", label: "Plateforme", render: c => PLATFORM_LABELS[c.device?.platform] || "—"},
        {key: "version", label: "Version", className: "tabular-nums", render: c => c.device?.appVersion || "—"},
        {key: "printers", label: "Imprimantes", className: "tabular-nums", render: c => (c.printers || []).length},
        {key: "backup", label: "Dernière sauvegarde", render: c => formatDateTime(lastBackupDate(c))},
    ];

    return (
        <div>
            <SectionActions>
                <Button variant="plain" size="sm" icon={RefreshCw} loading={isFetching} onClick={() => refetch()}>
                    Actualiser
                </Button>
            </SectionActions>
            <ReadOnlyNotice/>
            {isLoading ? (
                <ListLoader label="Chargement des appareils..."/>
            ) : error ? (
                <ErrorMessage error={error}/>
            ) : (
                <div>
                    <SettingsTable
                        columns={columns}
                        rows={configs}
                        onRowClick={c => setSelectedId(c.id)}
                        empty={{
                            icon: MonitorSmartphone,
                            title: "Aucun appareil",
                            text: "Aucune caisse n'a encore sauvegardé ses réglages. Sur la caisse : Réglages > Sauvegarde de la configuration.",
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default PosDevicesSettings;
